// Price formatting for the paywall surfaces (plan 10). Shared by the library
// unlock line and the upsell plate; kept in its own module so those component
// files export only components (react-refresh). The amount is integer cents,
// as attested by checkout — never a float.

/** A localized price string, e.g. `$9.00`. Falls back to a bare amount. */
export function formatPrice(amountCents: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amountCents / 100);
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/** One day, in ms — the unit the sale countdown speaks in. */
const DAY_MS = 86_400_000;

/**
 * How a sale's remaining time reads on a reader-facing surface: `"ends today"`,
 * `"ends tomorrow"`, `"3 days left"`, or a date past a week out. `null` when
 * there is no end (an open-ended sale has no deadline to press) or the window
 * has already closed — the caller is rendering a live sale, and a negative
 * countdown is a bug the reader should never be shown.
 *
 * DELIBERATELY COARSE. An hours-and-minutes countdown on a book is a pressure
 * tactic, and this shop sells one-time purchases of public-domain retellings.
 * Days are enough to be useful and honest.
 */
export function formatSaleEnds(
  saleEndsAt: number | null,
  now: number,
): string | null {
  if (saleEndsAt === null || !Number.isFinite(saleEndsAt)) return null;
  const remaining = saleEndsAt - now;
  if (remaining <= 0) return null;
  const days = Math.ceil(remaining / DAY_MS);
  if (days <= 1) return "ends today";
  if (days === 2) return "ends tomorrow";
  if (days <= 7) return `${String(days - 1)} days left`;
  try {
    return `ends ${new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(new Date(saleEndsAt))}`;
  } catch {
    return null;
  }
}

/**
 * Round a wall clock down to a `stepMs` boundary.
 *
 * WHY THE PRICE QUERY TAKES A COARSE `now`. `checkout.getProduct` needs the
 * current time to resolve a sale window, and a Convex query argument is part of
 * its subscription key: passing a raw `Date.now()` would mint a new
 * subscription on every render. Rounding to the minute makes the key stable,
 * at the cost of a sale flipping up to a minute late — which is the right
 * trade for a price that is re-attested at checkout anyway.
 */
export function coarsenNow(now: number, stepMs: number): number {
  if (stepMs <= 0 || !Number.isFinite(stepMs)) return now;
  return Math.floor(now / stepMs) * stepMs;
}
